import { canonicalAdminStatus } from "../screens/Admin/adminStatusUi";

/**
 * Maps request `aiAnalysis.status` to overview `ai` bucket keys.
 * @param {unknown} ai
 */
export function aiBucketFromAnalysis(ai) {
  const s = String(ai?.status || "").toLowerCase();
  if (s === "ok" || s === "ready" || s === "completed") return "ok";
  if (s === "error" || s === "failed") return "error";
  if (s === "pending" || s === "running" || s === "analysing" || s === "analyzing") return "pending";
  return "none";
}

function ensureOverviewShape(d) {
  if (!d) return false;
  d.counts = d.counts || {};
  d.unread = d.unread || { total: 0, requests: 0, contacts: 0 };
  d.requestsByStatus = d.requestsByStatus || {};
  d.contactsByStatus = d.contactsByStatus || {};
  d.ai = d.ai || { none: 0, pending: 0, ok: 0, error: 0, attempts: 0 };
  if (!Array.isArray(d.activity)) d.activity = [];
  return true;
}

function activityKind(it) {
  return it && it.kind === "contact" ? "contact" : "request";
}

/** @param {Record<string, unknown> | null | undefined} row */
function isUnreadRow(row) {
  if (!row) return false;
  if (typeof row.unread === "boolean") return row.unread;
  return !row.seenAt;
}

function isUnreadActivity(it) {
  if (!it) return false;
  if (typeof it.unread === "boolean") return it.unread;
  return !it.seenAt;
}

/**
 * @param {import("immer").Draft<Record<string, unknown>>} d
 * @param {string} id
 * @param {Record<string, unknown> | null} row snapshot from list/detail before delete
 */
export function removeServiceRequestFromOverview(d, id, row) {
  if (!ensureOverviewShape(d)) return;
  const sid = String(id);
  const actItem = d.activity.find(
    (it) => activityKind(it) === "request" && String(it.id) === sid
  );
  const wasUnread = row ? isUnreadRow(row) : actItem ? isUnreadActivity(actItem) : false;
  const statusKey = canonicalAdminStatus(
    /** @type {string} */ (row?.status ?? actItem?.status ?? "new")
  );

  d.activity = d.activity.filter(
    (it) => !(activityKind(it) === "request" && String(it.id) === sid)
  );
  d.counts.requests = Math.max(0, (Number(d.counts.requests) || 0) - 1);
  d.requestsByStatus[statusKey] = Math.max(
    0,
    (Number(d.requestsByStatus[statusKey]) || 0) - 1
  );

  if (wasUnread) {
    d.unread.requests = Math.max(0, (Number(d.unread.requests) || 0) - 1);
    d.unread.total = Math.max(0, (Number(d.unread.total) || 0) - 1);
  }

  const aiSource = row?.aiAnalysis ?? actItem?.aiAnalysis;
  const b = aiBucketFromAnalysis(aiSource);
  d.ai[b] = Math.max(0, (Number(d.ai[b]) || 0) - 1);
}

/**
 * @param {import("immer").Draft<Record<string, unknown>>} d
 * @param {string} id
 * @param {Record<string, unknown> | null} row
 */
export function removeContactMessageFromOverview(d, id, row) {
  if (!ensureOverviewShape(d)) return;
  const sid = String(id);
  const actItem = d.activity.find(
    (it) => activityKind(it) === "contact" && String(it.id) === sid
  );
  const wasUnread = row ? isUnreadRow(row) : actItem ? isUnreadActivity(actItem) : false;
  const statusKey = canonicalAdminStatus(
    /** @type {string} */ (row?.status ?? actItem?.status ?? "new")
  );

  d.activity = d.activity.filter(
    (it) => !(activityKind(it) === "contact" && String(it.id) === sid)
  );
  d.counts.contacts = Math.max(0, (Number(d.counts.contacts) || 0) - 1);
  d.contactsByStatus[statusKey] = Math.max(
    0,
    (Number(d.contactsByStatus[statusKey]) || 0) - 1
  );

  if (wasUnread) {
    d.unread.contacts = Math.max(0, (Number(d.unread.contacts) || 0) - 1);
    d.unread.total = Math.max(0, (Number(d.unread.total) || 0) - 1);
  }
}

/**
 * @param {import("immer").Draft<Record<string, unknown>>} d
 * @param {string} id
 * @param {Record<string, unknown>} prev row before patch
 * @param {Record<string, unknown>} patch API response / merge fields
 */
export function mergeServiceRequestUpdateIntoOverview(d, id, prev, patch) {
  if (!ensureOverviewShape(d) || !prev) return;
  const merged = { ...prev, ...patch };
  const oldSt = canonicalAdminStatus(/** @type {string} */ (prev.status));
  const newSt = canonicalAdminStatus(/** @type {string} */ (merged.status));
  if (oldSt !== newSt) {
    d.requestsByStatus[oldSt] = Math.max(0, (Number(d.requestsByStatus[oldSt]) || 0) - 1);
    d.requestsByStatus[newSt] = (Number(d.requestsByStatus[newSt]) || 0) + 1;
  }

  const oldUr = isUnreadRow(prev);
  const newUr = isUnreadRow(merged);
  if (oldUr !== newUr) {
    if (newUr) {
      d.unread.requests = (Number(d.unread.requests) || 0) + 1;
      d.unread.total = (Number(d.unread.total) || 0) + 1;
    } else {
      d.unread.requests = Math.max(0, (Number(d.unread.requests) || 0) - 1);
      d.unread.total = Math.max(0, (Number(d.unread.total) || 0) - 1);
    }
  }

  if (patch.aiAnalysis) {
    const ob = aiBucketFromAnalysis(prev.aiAnalysis);
    const nb = aiBucketFromAnalysis(merged.aiAnalysis);
    if (ob !== nb) {
      d.ai[ob] = Math.max(0, (Number(d.ai[ob]) || 0) - 1);
      d.ai[nb] = (Number(d.ai[nb]) || 0) + 1;
    }
  }

  const sid = String(id);
  const idx = d.activity.findIndex(
    (it) => activityKind(it) === "request" && String(it.id) === sid
  );
  if (idx >= 0) {
    const it = d.activity[idx];
    d.activity[idx] = {
      ...it,
      status: newSt,
      unread: newUr,
      ...(patch.typeLabel !== undefined ? { typeLabel: patch.typeLabel } : {}),
      ...(patch.title !== undefined ? { title: patch.title } : {}),
      ...(patch.replyCount !== undefined ? { replyCount: patch.replyCount } : {}),
      ...(patch.createdAt !== undefined ? { createdAt: patch.createdAt } : {}),
    };
  }
}

/**
 * @param {import("immer").Draft<Record<string, unknown>>} d
 * @param {string} id
 * @param {Record<string, unknown>} prev
 * @param {Record<string, unknown>} patch
 */
export function mergeContactMessageUpdateIntoOverview(d, id, prev, patch) {
  if (!ensureOverviewShape(d) || !prev) return;
  const merged = { ...prev, ...patch };
  const oldSt = canonicalAdminStatus(/** @type {string} */ (prev.status));
  const newSt = canonicalAdminStatus(/** @type {string} */ (merged.status));
  if (oldSt !== newSt) {
    d.contactsByStatus[oldSt] = Math.max(0, (Number(d.contactsByStatus[oldSt]) || 0) - 1);
    d.contactsByStatus[newSt] = (Number(d.contactsByStatus[newSt]) || 0) + 1;
  }

  const oldUr = isUnreadRow(prev);
  const newUr = isUnreadRow(merged);
  if (oldUr !== newUr) {
    if (newUr) {
      d.unread.contacts = (Number(d.unread.contacts) || 0) + 1;
      d.unread.total = (Number(d.unread.total) || 0) + 1;
    } else {
      d.unread.contacts = Math.max(0, (Number(d.unread.contacts) || 0) - 1);
      d.unread.total = Math.max(0, (Number(d.unread.total) || 0) - 1);
    }
  }

  const sid = String(id);
  const idx = d.activity.findIndex(
    (it) => activityKind(it) === "contact" && String(it.id) === sid
  );
  if (idx >= 0) {
    const it = d.activity[idx];
    d.activity[idx] = {
      ...it,
      status: newSt,
      unread: newUr,
      ...(patch.typeLabel !== undefined ? { typeLabel: patch.typeLabel } : {}),
      ...(patch.title !== undefined ? { title: patch.title } : {}),
      ...(patch.replyCount !== undefined ? { replyCount: patch.replyCount } : {}),
      ...(patch.createdAt !== undefined ? { createdAt: patch.createdAt } : {}),
      ...(patch.processing !== undefined ? { processing: patch.processing } : {}),
    };
  }
}

/**
 * Shift one request between Gemini overview buckets (socket or local merge).
 * @param {import("immer").Draft<Record<string, unknown>>} d
 * @param {string} fromBucket
 * @param {string} toBucket
 */
export function shiftOverviewAiBucket(d, fromBucket, toBucket) {
  if (!ensureOverviewShape(d) || fromBucket === toBucket) return;
  d.ai[fromBucket] = Math.max(0, (Number(d.ai[fromBucket]) || 0) - 1);
  d.ai[toBucket] = (Number(d.ai[toBucket]) || 0) + 1;
}

/**
 * @param {import("immer").Draft<Record<string, unknown>>} d
 * @param {string} id
 * @param {unknown} processing
 */
export function mergeContactProcessingIntoOverviewActivity(d, id, processing) {
  if (!ensureOverviewShape(d)) return;
  const sid = String(id);
  const idx = d.activity.findIndex(
    (it) => activityKind(it) === "contact" && String(it.id) === sid
  );
  if (idx < 0) return;
  d.activity[idx] = { ...d.activity[idx], processing };
}

/**
 * @param {import("immer").Draft<Record<string, unknown>>} d
 * @param {string} id
 * @param {unknown} aiAnalysis
 */
export function mergeRequestAiIntoOverviewActivity(d, id, aiAnalysis) {
  if (!ensureOverviewShape(d)) return;
  const sid = String(id);
  const idx = d.activity.findIndex(
    (it) => activityKind(it) === "request" && String(it.id) === sid
  );
  if (idx < 0) return;
  d.activity[idx] = { ...d.activity[idx], aiAnalysis };
}

/** @param {import("immer").Draft<Record<string, unknown>>} d */
export function bumpOverviewRepliesTotal(d) {
  if (!ensureOverviewShape(d)) return;
  d.counts.replies = (Number(d.counts.replies) || 0) + 1;
}

/**
 * @param {import("immer").Draft<Record<string, unknown>>} d
 * @param {string} id
 * @param {"request" | "contact"} kind
 */
export function bumpActivityReplyCount(d, id, kind) {
  if (!ensureOverviewShape(d)) return;
  const sid = String(id);
  const idx = d.activity.findIndex(
    (it) => activityKind(it) === kind && String(it.id) === sid
  );
  if (idx < 0) return;
  const it = d.activity[idx];
  const rc = Number(it.replyCount ?? it.reply_count) || 0;
  d.activity[idx] = { ...it, replyCount: rc + 1 };
}
